/**
 * Draft Quote Storage Utilities
 * Manages localStorage persistence for draft quotes with expiry
 */

const STORAGE_KEY = 'scheduler_draft_session';
const DRAFT_EXPIRY_DAYS = 30;

export interface DraftSession {
  contactId: string;
  timestamp: number;
}

/**
 * Save draft session to localStorage
 */
export const saveDraftToStorage = (contactId: string): void => {
  if (typeof window === 'undefined') return;
  
  const session: DraftSession = {
    contactId,
    timestamp: Date.now(),
  };
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    console.log('[Draft Storage] Saved to localStorage:', contactId);
  } catch (error) {
    console.error('[Draft Storage] Failed to save:', error);
  }
};

/**
 * Load draft session from localStorage
 * Returns null if expired or invalid
 */
export const loadDraftFromStorage = (): DraftSession | null => {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const session: DraftSession = JSON.parse(stored);
    
    // Check expiry
    const daysSinceCreation = (Date.now() - session.timestamp) / (1000 * 60 * 60 * 24);
    
    if (daysSinceCreation > DRAFT_EXPIRY_DAYS) {
      console.log('[Draft Storage] Draft expired, clearing');
      clearDraftFromStorage();
      return null;
    }
    
    return session;
  } catch (error) {
    console.error('[Draft Storage] Failed to load:', error);
    clearDraftFromStorage();
    return null;
  }
};

/**
 * Clear draft session from localStorage
 */
export const clearDraftFromStorage = (): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('[Draft Storage] Cleared from localStorage');
  } catch (error) {
    console.error('[Draft Storage] Failed to clear:', error);
  }
};

/**
 * Check if a stored draft exists
 */
export const hasDraftInStorage = (): boolean => {
  return loadDraftFromStorage() !== null;
};

